import React from 'react';
import { Handle, Position, useReactFlow, type NodeProps } from '@xyflow/react';

export interface FragmentNodeData {
    text: string;
    [key: string]: unknown;
}

/**
 * Fragment Node
 *
 * The core dialogue/narration node — "the heart of the tool" (doc 06 §1).
 * Supports inline edit on double-click, matching the writer-ergonomics
 * requirement in doc 06 §1 and the pattern already established by
 * condition.tsx/instruction.tsx: double-click opens an expanded editor
 * with a textarea + Save/Cancel, committed via `setNodes` so the parent
 * graph's autosave picks it up. Text can also be edited via the Inspector
 * sidebar (Inspector.tsx) — this adds the faster on-canvas path doc 06 §1
 * describes, it doesn't replace the sidebar one.
 */
export function FragmentNode({ data, selected, id }: NodeProps) {
    const nodeData = data as FragmentNodeData;
    const { setNodes } = useReactFlow();
    const [isEditing, setIsEditing] = React.useState(false);
    const [tempText, setTempText] = React.useState(nodeData.text);

    const handleSaveEdit = () => {
        setNodes((nodes) =>
            nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, text: tempText } } : n))
        );
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div
                className="nopan nodrag card card-sm shadow-lg"
                style={{ minWidth: 280, maxWidth: 360 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="card-status-top bg-info" />
                <div className="card-body py-2 px-3">
                    <label className="form-label small mb-1">Fragment Text</label>
                    <textarea
                        value={tempText}
                        onChange={(e) => setTempText(e.target.value)}
                        className="form-control form-control-sm"
                        rows={4}
                        autoFocus
                    />
                    <div className="d-flex gap-2 mt-2">
                        <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="btn btn-sm btn-success flex-fill"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setTempText(nodeData.text);
                                setIsEditing(false);
                            }}
                            className="btn btn-sm btn-outline-secondary flex-fill"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
            </div>
        );
    }

    return (
        <div
            className={`card card-sm ${selected ? 'border-primary shadow-sm' : ''}`}
            style={{ minWidth: 220, maxWidth: 320 }}
            onDoubleClick={() => setIsEditing(true)}
        >
            <div className="card-status-top bg-info" />
            <div className="card-body py-2 px-3">
                <div className="d-flex align-items-center gap-2 text-muted mb-1">
                    <i className="ti ti-message" />
                    <span className="text-uppercase text-xs">Fragment</span>
                </div>
                <div className="small nodrag" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {nodeData.text || '(empty)'}
                </div>
            </div>
            <Handle type="target" position={Position.Left} />
            <Handle type="source" position={Position.Right} />
        </div>
    );
}

export const fragmentNodeMeta = {
    type: 'fragment' as const,
    label: 'Fragment',
    icon: 'ti-message',
    defaultData: { text: 'Enter dialogue text here...' } satisfies FragmentNodeData,
};
